'use client';
import Button from '@mui/material/Button';
import GlobalStyles from '@mui/material/GlobalStyles';
import Stack from '@mui/material/Stack';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';

import cookieHelper from './cookieHelper';

const decodeHtmlEntities = (text: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, 'text/html');
  return doc.documentElement.textContent || text;
};

interface Prompt {
  queryKey: string;
  queryText: string;
  displayText: string;
  url: string;
  highlight?: string;
}

interface QuickFiltersData {
  prompts: Prompt[];
}

interface QuickFiltersProps {
  data?: QuickFiltersData;
  apiUrl?: string;
  pageType: string;
  stockNumber?: string;
  onQuickFilterClick?: (
    e: React.MouseEvent<HTMLButtonElement>,
    prompt: Prompt,
  ) => void;
  stackSx?: SxProps<Theme>;
  buttonSx?: SxProps<Theme>;
  highlightSx?: SxProps<Theme>;
  containerId?: string;
}

const QuickFilters = ({
  data: initialData,
  apiUrl,
  pageType,
  stockNumber,
  onQuickFilterClick,
  stackSx,
  buttonSx,
  highlightSx,
  containerId,
}: QuickFiltersProps) => {
  const [data, setData] = useState<QuickFiltersData | null>(
    initialData || null,
  );
  const [error, setError] = useState<string | null>(null);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const hasDragged = useRef(false);
  const startX = useRef(0);
  const scrollLeftRef = useRef(0);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!scrollContainerRef.current) return;
    isDragging.current = true;
    hasDragged.current = false;
    startX.current = e.pageX;
    scrollLeftRef.current = scrollContainerRef.current.scrollLeft;
  };

  const handleClickCapture = (e: React.MouseEvent) => {
    if (hasDragged.current) {
      e.preventDefault();
      e.stopPropagation();
      hasDragged.current = false;
    }
  };

  // Document-level listeners so drag keeps working even when cursor moves over
  // child buttons, and mouseup fires regardless of where the cursor lands.
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging.current || !scrollContainerRef.current) return;
      const walk = e.pageX - startX.current;
      if (Math.abs(walk) > 5) {
        hasDragged.current = true;
      }
      scrollContainerRef.current.scrollLeft = scrollLeftRef.current - walk;
    };

    const onMouseUp = () => {
      isDragging.current = false;
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  useEffect(() => {
    if (initialData) return;

    let isMounted = true;

    const fetchData = async () => {
      try {
        setError(null);

        if (typeof window !== 'undefined') {
          const storeId = cookieHelper.GetCookieItemValue('StoreId') || '';
          const baseUrl =
            apiUrl || `${window.location.origin}/cars/api/search/quickfilters`;
          const url = new URL(baseUrl);

          const params = new URLSearchParams({
            storeId,
            pageType,
          });

          if (stockNumber) {
            params.append('stockNumber', stockNumber);
          }

          url.search = params.toString();

          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`API request failed: ${response.status}`);
          }
          const responseData: QuickFiltersData = await response.json();

          if (isMounted) {
            const promptData = Array.isArray(responseData)
              ? { prompts: responseData }
              : responseData;
            setData(promptData);
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Unknown error');
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [initialData, apiUrl, pageType, stockNumber]);

  if (!data || error || !data.prompts || data.prompts.length === 0) {
    if (containerId) {
      return (
        <GlobalStyles
          styles={{
            [`#${containerId}`]: {
              display: 'none !important',
            },
          }}
        />
      );
    } else {
      return null;
    }
  }

  return (
    <Stack
      ref={scrollContainerRef}
      data-id="quick-links"
      direction="row"
      onMouseDown={handleMouseDown}
      onDragStart={(e) => e.preventDefault()}
      onClickCapture={handleClickCapture}
      sx={[
        {
          flexWrap: 'nowrap',
          overflowX: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          marginX: { xs: -3, sm: -5, md: -8 },
          paddingX: { xs: 3, sm: 5, md: 8 },
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' },
          '& *': { cursor: 'inherit' },
          userSelect: 'none',
        },
        ...(stackSx ? (Array.isArray(stackSx) ? stackSx : [stackSx]) : []),
      ]}
      spacing={0.5}
    >
      {data.prompts.map((prompt) => (
        <Button
          variant="outlined"
          color="primary"
          size="small"
          key={prompt.queryKey}
          onClick={
            onQuickFilterClick
              ? (e: React.MouseEvent<HTMLButtonElement>) =>
                  onQuickFilterClick(e, prompt)
              : undefined
          }
          href={prompt.url}
          sx={[
            ({ palette }) => ({
              color: palette.secondary.main,
              borderColor: palette.divider,
              flexShrink: 0,
              whiteSpace: 'nowrap',
              maxWidth: { xs: '200px', sm: '300px' },
            }),
            ...(buttonSx
              ? Array.isArray(buttonSx)
                ? buttonSx
                : [buttonSx]
              : []),
          ]}
        >
          <Typography component="span" variant="inherit" noWrap>
            {decodeHtmlEntities(prompt.displayText)}
          </Typography>
          {prompt.highlight && (
            <Typography
              variant="caption"
              color="white"
              sx={[
                {
                  // Using 'flagged.main' instead of theme overrides because flagged is not recognized by TS
                  backgroundColor: 'flagged.main',
                  borderRadius: '9999px',
                  px: 0.8125,
                  ml: 0.5,
                },
                ...(highlightSx
                  ? Array.isArray(highlightSx)
                    ? highlightSx
                    : [highlightSx]
                  : []),
              ]}
            >
              {decodeHtmlEntities(prompt.highlight)}
            </Typography>
          )}
        </Button>
      ))}
    </Stack>
  );
};

export default QuickFilters;
